<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex flex-wrap gap-4 items-center justify-between">
      <div>
        <PageTitle>
          Gerencie seus bots
        </PageTitle>
        <PageDescription>
          Controle e organize seus bots do Telegram com facilidade.
        </PageDescription>
      </div>
      <RegisterButton @click="openNewBotDialog">Cadastrar bot</RegisterButton>
    </div>

    <!-- Bots Table Card -->
    <TableContainer>
      <!-- Table Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Bots registrados</h2>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader class="w-full">
          <TableHead
            v-for="header in tableHeaders"
            :key="header.key"
            :class="header.class"
          >
            <div class="flex items-center gap-2">
              {{ header.label }}
              <ArrowsUpDownIcon 
                v-if="header.sortable"
                class="h-4 w-4 cursor-pointer text-zinc-500 hover:text-white"
                @click="sortBy(header.key)"
              />
            </div>
          </TableHead>
        </TableHeader>
        <TableBody>
          <template v-if="bots">
            <TableRow 
              v-for="bot in bots.data"
              :key="bot.id"
            >
              <TableCell class="flex items-center gap-3">
                <div class="relative group">
                  <img 
                    v-if="bot.media?.find(m => m.placement === 'profile_pic')" 
                    :src="bot.media?.find(m => m.placement === 'profile_pic')?.source" 
                    class="h-10 w-10 rounded-full object-cover border border-white/10 ring-2 ring-white/5 transition-transform group-hover:scale-105"
                  />
                  <div v-else class="h-10 w-10 rounded-full bg-zinc-800 flex items-center justify-center border border-white/10 ring-2 ring-white/5">
                    <BotIcon class="h-5 w-5 text-zinc-500" />
                  </div>
                </div>
                <div>
                  <div class="text-sm font-medium text-white">{{ bot.profileName || bot.name }}</div>
                </div>
              </TableCell>

              <TableCell>
                <div class="flex items-center gap-2">
                  <div class="truncate font-mono text-xs text-zinc-400">
                    {{ maskToken(bot.token) }}
                  </div>
                  <button
                    @click="copyToken(bot.token)"
                    class="rounded-lg bg-white/5 p-1.5 text-zinc-400 opacity-0 transition-all hover:bg-white/10 hover:text-white group-hover:opacity-100"
                  >
                    <ClipboardIcon class="h-4 w-4" />
                  </button>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-2">
                  <!-- <template> -->
                    <div class="w-48 truncate font-mono text-xs text-zinc-400">
                      {{ `https://bot.mybotexpress.com/${bot.uuid}` }}
                    </div>
                    <button
                      @click="copyBotLink(bot)"
                      class="rounded-lg bg-white/5 p-1.5 text-zinc-400 transition-all hover:bg-white/10 hover:text-white group-hover:opacity-100"
                    >
                      <ClipboardIcon class="h-4 w-4 text-white" />
                    </button>
                  <!-- </template> -->
                  <!-- <PulseBadge v-else variant="yellow">Nenhum token reserva configurado</PulseBadge> -->
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge :status="bot.status" />
              </TableCell>
              <TableCell>
                <div class="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    @click="editBot(bot)"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    @click="openCloneBotDialog(bot)"
                  >
                    <DocumentDuplicateIcon class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="hover:text-red-400"
                    @click="deleteBot(bot)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
            <TableFallback v-if="bots.data.length === 0" :colspan="tableHeaders.length" :icon="BotIcon" title="Nenhum bot encontrado" :description="`Clique em 'Cadastrar bot' para começar`" />
          </template>
          <TableRowSkeleton v-else :columns="tableHeaders.length" />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="bots">
        <template v-if="bots.data.length > 0">
          <MobileCard v-for="bot in bots.data" :key="bot.id">
             <template #header>
                <div class="flex items-center gap-3">
                  <div class="relative group">
                    <img 
                      v-if="bot.media?.find(m => m.placement === 'profile_pic')" 
                      :src="bot.media?.find(m => m.placement === 'profile_pic')?.source" 
                      class="h-10 w-10 rounded-full object-cover border border-white/10 ring-2 ring-white/5"
                    />
                    <div v-else class="h-10 w-10 rounded-full bg-zinc-800 flex items-center justify-center border border-white/10 ring-2 ring-white/5">
                      <BotIcon class="h-5 w-5 text-zinc-500" />
                    </div>
                  </div>
                  <div class="flex flex-col">
                   <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Bot</span>
                   <div class="flex items-center gap-2">
                     <span class="text-white font-bold text-lg">{{ bot.profileName || bot.name }}</span>
                   </div>
                  </div>
                </div>
             </template>

            
            <template #actions>
               <div class="flex flex-col items-end gap-2">
                  <div class="flex items-center gap-1">
                    <button @click="editBot(bot)" class="text-zinc-400 hover:text-white p-1">
                      <PencilIcon class="size-5" />
                    </button>
                    <button @click="openCloneBotDialog(bot)" class="text-zinc-400 hover:text-white p-1">
                      <DocumentDuplicateIcon class="size-5" />
                    </button>
                    <button @click="deleteBot(bot)" class="text-zinc-400 hover:text-red-400 p-1">
                      <TrashIcon class="size-5" />
                    </button>
                  </div>
               </div>
            </template>
            
            <template #content>
               <MobileDataLabel label="Token">
                  <div class="flex items-center gap-2 bg-white/5 p-2 rounded justify-between">
                     <div class="font-mono text-xs text-zinc-400 truncate max-w-[250px]">
                       {{ maskToken(bot.token) }}
                     </div>
                     <button
                        @click="copyToken(bot.token)"
                        class="rounded-lg p-1.5 text-zinc-400 transition-all hover:bg-white/10 hover:text-white"
                      >
                        <ClipboardIcon class="size-4 text-white" />
                      </button>
                  </div>
               </MobileDataLabel>
               
               <MobileDataLabel label="Link do bot">
                  <div class="flex items-center gap-2 bg-white/5 p-2 rounded justify-between">
                     <div class="font-mono text-xs text-zinc-400 truncate max-w-[250px]">
                       {{ `https://bot.mybotexpress.com/${bot.uuid}` }}
                     </div>
                     <button
                        @click="copyBotLink(bot)"
                        class="rounded-lg p-1.5 text-zinc-400 transition-all hover:bg-white/10 hover:text-white"
                      >
                        <ClipboardIcon class="size-4 text-white" />
                      </button>
                  </div>
               </MobileDataLabel>

               <div class="flex items-center justify-between pt-2">
                  <div class="flex flex-col">
                    <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Status</span>
                    <StatusBadge :status="bot.status" />
                  </div>
               </div>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <BotIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhum bot encontrado
        </div>
      </div>

      <!-- Pagination -->
      <Pagination @update:page="page => reloadAndGetAll({ page, perPage })" v-if="bots?.pagination" :data="bots?.pagination" v-model:per-page="perPage" />
    </TableContainer>
  </main>

  <DeleteConfirmationDialog v-model:open="isDeleteBotDialogOpen" :loading="isDeletingBot" @confirm="isDeletingBot = true; destroy(deletingBot!).then(() => {
    toast({
      title: 'Bot excluído com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
    isDeletingBot = false
    isDeleteBotDialogOpen = false
    const index = bots?.data.findIndex(bot => bot.id == deletingBot)
    if (index !== -1) bots?.data.splice(index!, 1)
  })" />

  <!-- Clone Bot Dialog -->
  <Dialog v-model:open="isCloneBotDialogOpen">
    <DialogScrollContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>Clonar bot</DialogTitle>
        <DialogDescription>
          Cria um bot novo com todas as mensagens, mídias, botões, planos, order bumps, pacotes e vídeo chamadas de
          <strong class="text-white">{{ cloningBot?.profileName || cloningBot?.name }}</strong>.
          Você só precisa informar o token do bot novo (gerado pelo @BotFather).
        </DialogDescription>
      </DialogHeader>

      <div class="grid gap-4 py-2">
        <div class="grid gap-2">
          <Label>Token do novo bot <span class="text-red-500">*</span></Label>
          <Input v-model="cloneToken" placeholder="Insira o token fornecido pelo @BotFather" />
        </div>

        <div class="rounded-md border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-200/80">
          Grupos/canais não são clonados automaticamente: o bot novo precisa ser adicionado como administrador
          nos mesmos grupos do bot original antes de vinculá-los aos planos clonados.
        </div>

        <p v-if="cloneError" class="text-xs text-red-400">{{ cloneError }}</p>
      </div>

      <DialogFooter>
        <Button variant="ghost" class="text-white" @click="closeCloneBotDialog">Cancelar</Button>
        <Button :disabled="isCloningBot || !cloneToken" @click="confirmCloneBot">
          <Loader2 v-if="isCloningBot" class="w-4 h-4 animate-spin mr-2" />
          Clonar bot
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

  <!-- New Bot Dialog -->
  <Dialog v-model:open="isBotDialogOpen" @close="closeNewBotDialog">
    <DialogScrollContent class="max-w-3xl " :class="[botForm.type && botForm.type === BotTypeEnum.DEFAULT ? 'lg:max-w-6xl ' : 'lg:max-w-3xl' ]">
      <DialogHeader>
        <DialogTitle>{{ editingBot ? 'Editar bot' : 'Novo bot' }}</DialogTitle>
        <DialogDescription>
          Insira as informações do seu bot do Telegram para começar.
        </DialogDescription>
      </DialogHeader>

      <div class="grid gap-6" :class="[botForm.type && botForm.type === BotTypeEnum.DEFAULT ? 'lg:grid-cols-12 ' : 'lg:grid-cols-7' ]">
        <Tabs defaultValue="general" class="w-full col-span-7" v-model="tab">
          <TabsList class="grid grid-cols-3 w-full">
            <TabsTrigger value="general">Geral</TabsTrigger>
            <TabsTrigger value="pix">
              <div class="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M20.9498 14.55L14.5598 20.94C13.1598 22.34 10.8598 22.34 9.44978 20.94L3.05977 14.55C1.65977 13.15 1.65977 10.85 3.05977 9.44001L9.44978 3.05C10.8498 1.65 13.1498 1.65 14.5598 3.05L20.9498 9.44001C22.3498 10.85 22.3498 13.15 20.9498 14.55Z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6.25 6.25L17.75 17.75" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17.75 6.25L6.25 17.75" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Configurações de Pix
              </div>
            </TabsTrigger>
            <TabsTrigger value="commands">
              <div class="flex items-center gap-1">
                <SquareTerminal />
                Comandos
              </div>
            </TabsTrigger>
            <TabsTrigger value="profile">
              <div class="flex items-center gap-1">
                <User />
                Perfil
              </div>
            </TabsTrigger>
            <!-- <TabsTrigger value="ai">Inteligência Artificial</TabsTrigger> -->
          </TabsList>
    
          <!-- Aba Geral -->
          <TabsContent value="general">
            <div class="grid sm:grid-cols-2 gap-6 py-4">
              <div v-if="!paymentGatewayIntegration && !bots?.data.length" class="grid gap-2 col-span-full">
                <div class="flex items-center gap-2">
                  <Label>Código de indicação</Label>
                  <TooltipProvider>
                    <Tooltip :delay-duration="100">
                      <TooltipTrigger as-child>
                        <CircleHelp class="size-5 text-primary" />
                      </TooltipTrigger>
                      <TooltipContent side="top" :side-offset="5">
                        Insira um código de indicação para economizar
                        em taxas por cada venda. Após a criação do seu primeiro bot, não <br>
                        será mais possível preencher este campo.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input v-model="botForm.referringOrganizationUUID" placeholder="Ex: 87c3ab49-90be-4216-848c-e0dcee074b42" />
              </div>
              <div v-if="botForm.name" class="grid gap-2 col-span-full">
                <Label>Username <span class="text-red-500">*</span></Label>
                <Input v-model="botForm.name" placeholder="Ex: Meu Bot de Vendas" disabled />
              </div>
              <div class="grid gap-2" :class="[!botForm.name ? 'col-span-full' : '']">
                <Label>Token do bot <span class="text-red-500">*</span></Label>
                <Input v-model="botForm.token" placeholder="Insira o token fornecido pelo @BotFather" />
              </div>
              <div class="grid gap-2" :class="[!botForm.name ? 'col-span-full' : '']">
                <Label>Token Reserva (Contingência)</Label>
                <Input v-model="botForm.backupToken" placeholder="Caso o seu bot caia o token reserva substituirá o seu bot atual" />
              </div>
              <div class="grid gap-2 col-span-full">
                <div class="flex items-center gap-2">
                  <Switch v-model:checked="botForm.cloakerEnabled" />
                  <Label>Cloaker + Anticlone</Label>
                </div>
                <p class="text-[10px] text-zinc-500">
                  Protege o link do seu bot: só deixa passar quem acessar através do redirecionador configurado para esse bot,
                  bloqueando acesso direto ao link, links reaproveitados/vazados e ferramentas automatizadas de cópia.
                </p>
              </div>
              <div class="grid gap-2 col-span-full">
                <Label>Tipo <span class="text-red-500">*</span></Label>
                <Select :model-value="botForm.type" @update:model-value="(type) => { hasChangedType = true; botForm.type = type as BotTypeEnum; }">
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem :value="BotTypeEnum.DEFAULT">Padrão</SelectItem>
                    <SelectItem :value="BotTypeEnum.CHAT_FLOW">Fluxo de Chat</SelectItem>
                  </SelectContent>
                </Select>
                <div v-if="botForm.type === BotTypeEnum.CHAT_FLOW && hasChangedType" class="mt-2 p-2 bg-zinc-500/5 border border-zinc-500/20 rounded-md text-zinc-400 text-xs">
                  Vendas realizadas dentro do fluxo de chat terão uma taxa adicional de R$ 0,40 por venda.
                </div>
              </div>
              <template v-if="botForm.type && botForm.type === BotTypeEnum.DEFAULT">
                <div class="grid gap-4">
                  <FileUpload 
                    v-model:preview="startMediaPreview" 
                    v-model="botForm.startMedia" 
                    v-model:has-spoiler="botForm.startMediaHasSpoiler"
                    label="Mídia inicial" 
                  />
                </div>
                <div class="grid gap-4">
                  <FileUpload 
                    v-model:preview="topMediaPreview" 
                    v-model="botForm.topMedia" 
                    v-model:has-spoiler="botForm.topMediaHasSpoiler"
                    label="Mídia superior" 
                  />
                </div>
                <div class="grid gap-2 sm:col-span-2">
                  <Label class="flex items-center gap-2">
                    Mensagem inicial
                    <TooltipProvider>
                      <Tooltip :delay-duration="100">
                        <TooltipTrigger as-child>
                          <CircleHelp class="size-5 text-primary" />
                        </TooltipTrigger>
                        <TooltipContent side="top" :side-offset="5">
                          Insira a mensagem que será exibida <br> quando o usuário iniciar o bot.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <RichTextarea 
                    v-model="botForm.startMessage" 
                    placeholder="Ex: Este bot..." 
                    class="min-h-[200px]"
                    :variables="[
                      { label: 'Nome', value: '{{nome}}' },
                      { label: 'Cidade', value: '{{cidade}}' },
                      { label: 'Estado', value: '{{estado}}' }
                    ]"
                  />
                </div>
                <div class="col-span-full">
                  <MultiAudioUpload v-model="audios" :previews="audiosPreview" v-model:delete-media-ids="deletedMediaIds" />
                </div>
                <div class="grid gap-2 sm:col-span-2">
                  <Label class="flex items-center gap-2">
                    Mensagem de planos
                    <TooltipProvider>
                      <Tooltip :delay-duration="100">
                        <TooltipTrigger as-child>
                          <CircleHelp class="size-5 text-primary" />
                        </TooltipTrigger>
                        <TooltipContent side="top" :side-offset="5">
                          Mensagem que será exibida quando o <br> usuário clica no botão de exibir planos.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <RichTextarea 
                    v-model="botForm.paymentPlansMessage" 
                    placeholder="Ex: Temos os planos..." 
                    class="min-h-[150px]"
                    :variables="[
                      { label: 'Nome', value: '{{nome}}' },
                      { label: 'Cidade', value: '{{cidade}}' },
                      { label: 'Estado', value: '{{estado}}' }
                    ]"
                  />
                </div>
                <div v-if="showActionButton" class="grid gap-2 sm:col-span-2">
                  <Label>Texto do botão de exibir planos</Label>
                  <Input v-model="botForm.actionButtonLabel" placeholder="Ex: Ver planos" />
                </div>
                <div class="flex gap-6 sm:col-span-2 flex-row">
                  <div class="flex items-center gap-2">
                    <Switch :checked="botForm.status === BotStatusEnum.ACTIVE" @update:checked="checked => botForm.status = checked ? BotStatusEnum.ACTIVE : BotStatusEnum.INACTIVE" />
                    <Label>{{ botForm.status === BotStatusEnum.ACTIVE ? 'Ativo' : 'Inativo' }}</Label>
                  </div>
                  <div class="flex items-center gap-2">
                    <Switch 
                      :checked="showActionButton" 
                      @update:checked="(val) => { 
                        showActionButton = val; 
                        if (val && !botForm.actionButtonLabel) botForm.actionButtonLabel = 'Ver planos'; 
                        if (!val) botForm.actionButtonLabel = ''; 
                      }" 
                    />
                    <Label>Botão de ver planos</Label>
                  </div>
                </div>
              </template>
            </div>
          </TabsContent>
    
          <!-- Aba Pix -->
          <TabsContent value="pix">
            <div class="grid sm:grid-cols-2 gap-6 py-4">
              <div class="grid gap-2 sm:col-span-2">
                <Label>Mensagem enquanto gera o Pix</Label>
                <Input v-model="botForm.generatingPixMessage" placeholder="Ex: Gerando seu Pix, aguarde..." />
              </div>
              <div class="grid gap-2 sm:col-span-2">
                <Label class="flex items-center gap-2">
                  Instrução para pagamento do Pix
                  <TooltipProvider>
                    <Tooltip :delay-duration="100">
                      <TooltipTrigger as-child>
                        <CircleHelp class="size-5 text-primary" />
                      </TooltipTrigger>
                      <TooltipContent side="top" :side-offset="5">
                        <span class="text-primary" v-html="'{{plano}}'"></span>: Nome do plano<br>
                        <span class="text-primary" v-html="'{{valor}}'"></span>: Valor do pix<br>
                        <span class="text-primary" v-html="'{{order_bump}}'"></span>: Order bump
                        <!-- <span class="text-primary" v-html="'{{prazo}}'"></span>: Prazo de pagamento do pix -->
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <RichTextarea 
                  v-model="botForm.pixPaymentInstruction" 
                  placeholder="Ex: Escaneie o QR Code ou copie e cole o código abaixo." 
                  class="min-h-[150px]"
                  :variables="[
                    { label: 'Plano', value: '{{plano}}' },
                    { label: 'Valor', value: '{{valor}}' },
                    { label: 'Order Bump', value: '{{order_bump}}' }
                  ]"
                />
              </div>
              <div class="grid gap-2 sm:col-span-2">
                <Label>Texto do botão para verificar pagamento</Label>
                <Input v-model="botForm.pixVerifyButtonLabel" placeholder="Ex: Verificar pagamento" />
              </div>
              <div class="grid gap-2 sm:col-span-2">
                <Label>Instrução após gerar Pix</Label>
                <RichTextarea 
                  v-model="botForm.pixGeneratedInstruction" 
                  placeholder="Ex: Após o pagamento clique no link para acessar o grupo." 
                  class="min-h-[150px]"
                  :variables="[
                    { label: 'Nome', value: '{{nome}}' }
                  ]"
                />
              </div>
              <div class="grid gap-2 sm:col-span-2">
                <Label>Mensagem de pagamento não identificado</Label>
                <Input v-model="botForm.pixPaymentStatusMessage" placeholder="Ex: Pagamento não identificado!" />
              </div>
              <div class="flex items-center gap-2">
                <Switch v-model:checked="botForm.displayPixQrCode" />
                <Label>Exibir QR Code do Pix</Label>
              </div>
            </div>
          </TabsContent>
          <!-- <TabsContent value="ai">
            <div class="grid sm:grid-cols-2 gap-6 py-4">
              <div class="grid gap-2">
                <Label>Token <span class="text-red-500">*</span></Label>
                <Input v-model="botForm.aiToken" placeholder="Insira o token fornecido pela OPEN AI" />
              </div>
              <div class="grid gap-2">
                <Label>Temperatura (Criatividade da IA) <span class="text-red-500">*</span></Label>
                <Input type="number" max="1" min="0" step="0.01" v-model="botForm.aiTemperature" placeholder="Ex: 0.5" />
              </div>
              <div class="grid gap-2 col-span-full">
                <Label>Personalidade</Label>
                <Textarea class="min-h-[100px]" v-model="botForm.aiPersonality" placeholder="Ex: Você é um vendedor carismático que entende tudo sobre suplementos." />
              </div>
            </div>
          </TabsContent> -->
          <TabsContent value="commands">
            <div class="grid gap-6 py-6">
              <div class="flex items-center gap-2">
                <Switch v-model:checked="botForm.allowSubscriptionsCommand" />
                <Label>/assinaturas</Label>
              </div>

              <div class="grid gap-4">
                <div class="flex items-center gap-2">
                  <Switch v-model:checked="supportCommand.enabled" />
                  <Label>/suporte</Label>
                </div>

                <div v-if="supportCommand.enabled" class="grid gap-2">
                  <Label>Username do Telegram de suporte</Label>
                  <Input v-model="supportCommand.username" placeholder="Ex: @suporte_botexpress" />
                  <p class="text-[10px] text-zinc-500">Ao usar /suporte, o bot enviará um botão que leva o cliente até esse usuário/canal no Telegram.</p>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="profile">
            <div class="grid sm:grid-cols-2 gap-6 py-4">
              <div v-if="editingBot" class="grid gap-2 col-span-full">
                <Label>Nome do Bot</Label>
                <Input v-model="botForm.profileName" placeholder="Ex: Meu Bot de Vendas" />
                <p class="text-[10px] text-zinc-500">Este é o nome de exibição que aparece no perfil do bot no Telegram.</p>
              </div>
              <div class="grid gap-4 col-span-full">
                <FileUpload
                  v-model:preview="profilePicPreview"
                  v-model="botForm.profilePic"
                  label="Foto de Perfil"
                  :show-spoiler="false"
                />
                <p class="text-[10px] text-zinc-500">A foto de perfil do seu bot.</p>
              </div>
              <div class="grid gap-2 col-span-full">
                <Label class="flex items-center gap-2">
                  Sobre (Bio)
                </Label>
                <Textarea 
                  v-model="botForm.profileAbout" 
                  placeholder="Ex: Assine agora e tenha acesso ao melhor conteúdo!" 
                  class="min-h-[80px]"
                  maxlength="120"
                />
                <div class="flex justify-between items-center text-[10px] text-zinc-500">
                  <p>Uma breve descrição do seu bot que aparece no perfil (Bio). Máx 120 caracteres.</p>
                  <span>{{ (botForm.profileAbout?.length || 0) }}/120</span>
                </div>
              </div>
              <div class="grid gap-2 col-span-full">
                <Label class="flex items-center gap-2">
                  Descrição
                </Label>
                <Textarea 
                  v-model="botForm.profileDescription" 
                  placeholder="Ex: Este bot ajuda você a gerenciar suas assinaturas..." 
                  class="min-h-[120px]"
                  maxlength="512"
                />
                <div class="flex justify-between items-center text-[10px] text-zinc-500">
                  <p>A descrição que os usuários veem quando encontram o bot e ainda não iniciaram. Máx 512 caracteres.</p>
                  <span>{{ (botForm.profileDescription?.length || 0) }}/512</span>
                </div>
              </div>
            </div>
          </TabsContent>

        </Tabs>
        <div v-if="botForm.type && botForm.type === BotTypeEnum.DEFAULT" class="w-full col-span-7 md:col-span-5">
          <TelegramMessagePreview :disableInput="true" :messages="botMessagesPreview as MessagePreviewProp[]" :sender="botForm.profileName || botForm.name" />
        </div>
      </div>
  
      <DialogFooter>
        <Button class="text-white" variant="ghost" @click="closeNewBotDialog">Cancelar</Button>
        <Button :disabled="isSaving || !botForm.token" @click="saveBot">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          {{ editingBot ? 'Salvar alterações' : 'Cadastrar bot' }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import {
  ArrowsUpDownIcon,
  // RobotIcon,
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  DocumentDuplicateIcon,
} from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BotIcon, Loader2, CircleHelp, SquareTerminal, User } from 'lucide-vue-next'

import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import Table from '@/components/ui/table/Table.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { CreateBotDTO, GetAllBotsParams, useBot } from '@/composables/useBot'
import { Bot } from '@/interfaces/Bot'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import StatusBadge from '@/components/ui/badge/StatusBadge.vue'
import { useToast } from '@/components/ui/toast'
import { Switch } from '@/components/ui/switch'
import { BotStatusEnum } from '@/enums/bots/BotStatusEnum'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { useRoute } from 'vue-router'
import FileUpload from '@/components/ui/upload/FileUpload.vue'
import { MediaPlacementEnum } from '@/enums/media/MediaPlacementEnum'
import TelegramMessagePreview, { MessagePreviewProp } from '@/components/ui/telegram/TelegramMessagePreview.vue'
import { usePaymentGatewayIntegration } from '@/composables/usePaymentGatewayIntegration'
import MultiAudioUpload from '@/components/ui/upload/MultiAudioUpload.vue'
import { MediaTypeEnum } from '@/enums/media/MediaTypeEnum'
import { Media } from '@/interfaces/Media'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BotTypeEnum, BotTypeEnumLabels } from '@/enums/bots/BotTypeEnum'
import { BotCommandActionTypeEnum } from '@/enums/bots/BotCommandActionTypeEnum'
import { BotCommand } from '@/interfaces/BotCommand'
import RichTextarea from '@/components/ui/textarea/RichTextarea.vue'
// import { useOrganization } from '@/composables/useOrganization'

// Table Configuration
const tableHeaders = [
  { key: 'name', label: 'Nome', sortable: true },
  { key: 'token', label: 'Token', sortable: false },
  { key: 'link', label: 'Link', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'actions', label: '', class: 'w-32' },
]

// const { isBetaTester } = useOrganization()
const { paymentGatewayIntegration } = usePaymentGatewayIntegration()
const tab = ref<'general' | 'pix'>('general')
const { getAll, create, update, destroy, clone, selectableBots, selectBot, selectedBot } = useBot()
const isSaving = ref(false)
const botForm = ref<Partial<CreateBotDTO>>({
  status: BotStatusEnum.ACTIVE,
})
const audios = ref<{ file: File | null, title: string, previewUrl: string }[]>([])
const editingBot = ref<Bot | undefined>()
const isDeletingBot = ref(false)
const isDeleteBotDialogOpen = ref(false)
const deletingBot = ref<number>()
const isCloneBotDialogOpen = ref(false)
const isCloningBot = ref(false)
const cloningBot = ref<Bot>()
const cloneToken = ref('')
const cloneError = ref('')
const { toast } = useToast()
const perPage = ref('10')
const route = useRoute()
const startMediaPreview = ref<string>()
const topMediaPreview = ref<string>()
const audiosPreview = ref<Media[]>([])
const deletedMediaIds = ref<number[]>([])
const profilePicPreview = ref<string>()
const showActionButton = ref(false)
const supportCommand = ref<{ enabled: boolean, username?: string }>({
  enabled: false
})

const hasChangedType = ref(false)
const botMessagesPreview = computed(() => {
  const messages = []

  if (tab.value === 'general') {
    if (botForm.value.startMedia ?? startMediaPreview.value) {
      const editingBotStartMedia = editingBot.value?.media?.find(media => media.placement === MediaPlacementEnum.START)

      messages.push({
        type: botForm.value.startMedia ? botForm.value.startMedia.type.split('/')[0] : editingBotStartMedia!.type,
        source: botForm.value.startMedia ? URL.createObjectURL(botForm.value.startMedia) : startMediaPreview.value
      })
    }

    if(botForm.value.startMessage) {
      messages.push({
        type: 'text',
        text: botForm.value.startMessage
      })
    }

    if (botForm.value.actionButtonLabel && showActionButton.value) {
      messages.push({
        type: 'button',
        text: botForm.value.actionButtonLabel
      })
    }

    if (botForm.value.topMedia ?? topMediaPreview.value) {
      const editingBotTopMedia = editingBot.value?.media?.find(media => media.placement === MediaPlacementEnum.TOP)

      messages.push({
        type: botForm.value.topMedia ? botForm.value.topMedia.type.split('/')[0] : editingBotTopMedia!.type,
        source: botForm.value.topMedia ? URL.createObjectURL(botForm.value.topMedia) : topMediaPreview.value
      })
    }

    if (audios.value.length) {
      audios.value.forEach(audio => {
        if (audio.previewUrl || audio.file) {
          messages.push({
            type: 'audio',
            source: audio.previewUrl ?? URL.createObjectURL(audio.file!)
          })
        }
      })
    }
  
    if (botForm.value.paymentPlansMessage && !showActionButton.value) {
      messages.push({
        type: 'text',
        text: botForm.value.paymentPlansMessage
      })
      messages.push({
        type: 'button',
        text: 'Plano de exemplo'
      })
      messages.push({
        type: 'button',
        text: 'Plano de exemplo'
      })
      messages.push({
        type: 'button',
        text: 'Plano de exemplo'
      })
      messages.push({
        type: 'button',
        text: 'Link de exemplo',
        isRedirectLink: true
      })
    }
  } else {
    if (botForm.value.generatingPixMessage) {
      messages.push({
        type: 'text',
        text: botForm.value.generatingPixMessage
      })
    }

    if (botForm.value.pixPaymentInstruction) {
      messages.push({
        type: 'text',
        text: botForm.value.pixPaymentInstruction
      })
    }

    if (botForm.value.displayPixQrCode) {
      messages.push({
        type: 'image',
        source: '/assets/bot/pix/qr-code-example.jpg',
      })
    }

    messages.push({
      type: 'text',
      text: '00020126850014br.gov.bcb.pix2563pix.exemplo.com.br/qr/v3/at/48d9c620-9eaj5-4972-8e98-f94881fee4705204000053039865802BR5925HOOPAY_TECNOLOGIA_SERVICO6008BRASILIA62070503***63042452',
      copyable: true
    })
    
    if (botForm.value.pixVerifyButtonLabel) {
      messages.push({
        type: 'button',
        text: botForm.value.pixVerifyButtonLabel
      })
    }

    if (botForm.value.pixGeneratedInstruction) {
      messages.push({
        type: 'text',
        text: botForm.value.pixGeneratedInstruction
      })
    }
  }

  return messages
})

// Sample data (empty for demo)
const bots = ref<IPagination<Bot>>()

// Dialog
const isBotDialogOpen = ref(false)

// Utils
const maskToken = (token: string) => {
  return `${token.slice(0, 8)}...${token.slice(-8)}`
}

const normalizeTelegramUsername = (input?: string) => {
  return (input ?? '').trim().replace(/^https?:\/\/t\.me\//i, '').replace(/^@/, '')
}

const copyToken = async (token: string) => {
  await navigator.clipboard.writeText(token)
  toast({
    title: 'Token copiado para a área de transferência',
    class: 'bg-green-500 text-white',
    duration: 1500
  })
}

const copyBotLink = async (bot: Bot) => {
  const link = `https://bot.mybotexpress.com/${bot.uuid}`
  await navigator.clipboard.writeText(link)
  toast({
    title: 'Link copiado para a área de transferência',
    class: 'bg-green-500 text-white',
    duration: 1500
  })
}

// Actions
const openNewBotDialog = () => {
  isBotDialogOpen.value = true
  editingBot.value = undefined
  audios.value = []
  deletedMediaIds.value = []
  audiosPreview.value = []
  botForm.value = { 
    status: BotStatusEnum.ACTIVE, 
    generatingPixMessage: 'Aguarde um momento...', 
    pixPaymentInstruction: 
    `✅ PIX gerado com sucesso!
🎯 {{plano}} + {{order_bump}}
💰 Valor da transação: {{valor}}

📱 Para efetuar o pagamento, basta selecionar a opção "Pagar" no seu aplicativo bancário e escolher "PIX Copia e Cola".

💠 Pix copia e cola:`,
    pixVerifyButtonLabel: 'Verificar pagamento'
  }
  supportCommand.value = { enabled: false }
  startMediaPreview.value = undefined
  topMediaPreview.value = undefined
  profilePicPreview.value = undefined
}


const closeNewBotDialog = () => {
  isBotDialogOpen.value = false
}

const sortBy = (key: string) => {
  // Implement sorting logic
  console.log('Sorting by:', key)
}

const saveBot = async () => {
  isSaving.value = true

  const commands: BotCommand[] = []
  if (supportCommand.value.enabled || supportCommand.value.username) {
    const username = normalizeTelegramUsername(supportCommand.value.username)
    commands.push({
      command: 'suporte',
      description: 'Fala com o nosso suporte',
      enabled: supportCommand.value.enabled,
      type: BotCommandActionTypeEnum.LINK,
      link: username ? `https://t.me/${username}` : ''
    })
  }
  botForm.value.commands = commands

  try {
    if (!editingBot.value) {
      await create({ ...botForm.value, audios: audios.value.map(audio => audio.file) } as CreateBotDTO)
      toast({
        title: 'Bot criado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      closeNewBotDialog()
      bots.value = await getAll()
    } else {
      let payload: any = { ...botForm.value, audios: audios.value.map(audio => audio.file), deletedMediaIds: deletedMediaIds.value }

      const startMedia = editingBot.value.media?.find(media => media.placement === MediaPlacementEnum.START)
      const topMedia = editingBot.value.media?.find(media => media.placement === MediaPlacementEnum.TOP)
      const profilePic = editingBot.value.media?.find(media => media.placement === MediaPlacementEnum.PROFILE_PIC)

      if (startMedia && !startMediaPreview.value) {
        payload.deletedMediaIds?.push(startMedia.id)
      }

      if (topMedia && !topMediaPreview.value) {
        payload.deletedMediaIds?.push(topMedia.id)
      }

      if (profilePic && !profilePicPreview.value) {
        payload.deletedMediaIds?.push(profilePic.id)
      }


      if (!payload.deletedMediaIds?.length) {
        delete payload.deletedMediaIds
      }

      if (!showActionButton.value) {
        payload.actionButtonLabel = ''
      }

      await update(editingBot.value.id, payload)
      toast({
        title: 'Bot editado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      closeNewBotDialog()

      // const index = bots.value?.data.findIndex(bot => bot.id === editingBot.value)
      // if (index !== -1 && index !== null && index !== undefined) {
      //   bots.value!.data[index] = {
      //     ...bots.value!.data[index],
      //     ...botForm.value
      //   }
      // }
      bots.value = await getAll()

      if (editingBot.value.id == selectedBot.value?.id && payload.type !== selectedBot.value?.type) {
        selectBot({ ...selectedBot.value, type: payload.type } as unknown as Bot)
      }
    }
  } finally {
    isSaving.value = false
  }
}

const editBot = (bot: Bot) => {
  editingBot.value = bot
  botForm.value = {
    name: bot.name,
    token: bot.token,
    backupToken: bot.backupToken,
    type: bot.type,
    startMessage: bot.startMessage,
    paymentPlansMessage: bot.paymentPlansMessage,
    actionButtonLabel: bot.actionButtonLabel,
    generatingPixMessage: bot.generatingPixMessage,
    pixGeneratedInstruction: bot.pixGeneratedInstruction,
    pixPaymentInstruction: bot.pixPaymentInstruction,
    pixPaymentStatusMessage: bot.pixPaymentStatusMessage,
    pixVerifyButtonLabel: bot.pixVerifyButtonLabel,
    status: bot.status,
    displayPixQrCode: bot.displayPixQrCode,
    allowSubscriptionsCommand: bot.allowSubscriptionsCommand,
    cloakerEnabled: bot.cloakerEnabled,
    profileName: bot.profileName,
    profileDescription: bot.profileDescription,
    profileAbout: bot.profileAbout,
    startMediaHasSpoiler: bot.media?.find(m => m.placement === MediaPlacementEnum.START)?.hasSpoiler ?? false,
    topMediaHasSpoiler: bot.media?.find(m => m.placement === MediaPlacementEnum.TOP)?.hasSpoiler ?? false,
  }

  const existingSupportCommand = bot.commands?.find(command => command.command === 'suporte')
  supportCommand.value = existingSupportCommand
    ? {
      enabled: existingSupportCommand.enabled,
      username: normalizeTelegramUsername(existingSupportCommand.link)
    }
    : { enabled: false }

  startMediaPreview.value = ''
  topMediaPreview.value = ''
  profilePicPreview.value = ''

  audios.value = []
  deletedMediaIds.value = []
  let _audioPreviews: any[] = []

  bot.media?.forEach(media => {
    if (media.placement === MediaPlacementEnum.START) {
      startMediaPreview.value = media.source
    } else if (media.placement === MediaPlacementEnum.TOP) {
      topMediaPreview.value = media.source
    } else if (media.placement === MediaPlacementEnum.PROFILE_PIC) {
      profilePicPreview.value = media.source
    } else if (media.type === MediaTypeEnum.AUDIO) {
      _audioPreviews.push(media)
    }
  })

  audiosPreview.value = _audioPreviews
  showActionButton.value = bot.actionButtonLabel !== null && bot.actionButtonLabel !== undefined && bot.actionButtonLabel !== ''
  isBotDialogOpen.value = true
  tab.value = 'general'
}

const deleteBot = (bot: Bot) => {
  isDeleteBotDialogOpen.value = true
  deletingBot.value = bot.id
}

const openCloneBotDialog = (bot: Bot) => {
  cloningBot.value = bot
  cloneToken.value = ''
  cloneError.value = ''
  isCloneBotDialogOpen.value = true
}

const closeCloneBotDialog = () => {
  if (isCloningBot.value) return
  isCloneBotDialogOpen.value = false
}

const confirmCloneBot = async () => {
  if (!cloningBot.value || !cloneToken.value) return

  isCloningBot.value = true
  cloneError.value = ''

  try {
    await clone(cloningBot.value.id, cloneToken.value)
    toast({
      title: 'Bot clonado com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
    isCloneBotDialogOpen.value = false
    bots.value = await getAll()
  } catch (error: any) {
    cloneError.value = error?.response?.data?.errors?.[0]?.message
      || error?.response?.data?.message
      || 'Erro ao clonar bot. Verifique o token informado.'
  } finally {
    isCloningBot.value = false
  }
}

const reloadAndGetAll = (params: GetAllBotsParams) => {
  bots.value = undefined
  getAll(params).then((data) => bots.value = data)
}

watch(() => perPage.value, () => {
  reloadAndGetAll({ perPage: perPage.value })
})

onMounted(async () => {
  bots.value = await getAll()

  if (route.query.isBotDialogOpen) {
    openNewBotDialog()
  }
})
</script>